import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Menu, MenuItem, ListItemText } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { LANGUAGES, changeLanguage } from "../../../i18n";

/**
 * Language dropdown: a compact trigger that opens a menu of the supported
 * languages. Replaces the earlier two-state toggle now that three languages
 * (bg / en / de) are supported — a segmented control does not scale past two.
 *
 * The choice persists to localStorage, so it survives a reload and applies
 * before the user has authenticated.
 *
 * Rendered as its own MUI Menu rather than a native <select> so it stacks
 * correctly when opened from inside another Menu (the Header profile menu):
 * each MUI Menu owns its Popover layer, so the language list appears above the
 * profile menu without closing it.
 *
 * `sx` styles the OUTER wrapper (margins, alignment); the trigger sizes itself
 * to its content.
 */
function LanguageSwitch({ sx }) {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const current = LANGUAGES.includes(i18n.language)
    ? i18n.language
    : LANGUAGES[0];

  const handleOpen = (event) => {
    // Stop the click bubbling to a parent Menu, which would otherwise treat it
    // as an outside click and close before the language menu opens.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleSelect = (lng, event) => {
    if (event) event.stopPropagation();
    changeLanguage(lng);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: "inline-flex", ...sx }}>
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language.label")}
        onClick={handleOpen}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 10px",
          borderRadius: "8px",
          backgroundColor: "#F8F9FA",
          border: "1px solid #E3E5E8",
          color: "#1A1D1F",
          fontFamily: "inherit",
          fontSize: "0.8125rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          cursor: "pointer",
          transition: "background-color 0.2s, border-color 0.2s",
          "&:hover": {
            backgroundColor: "#F1F3F5",
            borderColor: "#D5D8DC",
          },
          "&:focus-visible": {
            outline: "2px solid #0DC1CE",
            outlineOffset: "2px",
          },
        }}
      >
        <span>{current.toUpperCase()}</span>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: "1.05rem",
            color: "#6F767E",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ role: "listbox", dense: true }}
        elevation={3}
      >
        {LANGUAGES.map((lng) => {
          const isActive = lng === current;
          return (
            <MenuItem
              key={lng}
              role="option"
              aria-selected={isActive}
              selected={isActive}
              onClick={(event) => handleSelect(lng, event)}
              sx={{ minWidth: "160px", gap: "10px", fontSize: "0.875rem" }}
            >
              <ListItemText
                primary={t(`language.${lng}`)}
                primaryTypographyProps={{
                  fontWeight: isActive ? 600 : 400,
                }}
              />
              {isActive && (
                <CheckIcon sx={{ fontSize: "1.1rem", color: "#0DC1CE" }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export default LanguageSwitch;
